import { Component, Input, Output, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'upload-products-dialog',
  templateUrl: './upload-products-dialog.component.html',
  styleUrls: ['./upload-products-dialog.component.scss'],
  standalone: false
})
export class UploadProductsDialogComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() riderId!: number;

  @Output() hideDialog = new EventEmitter<boolean>();
  @Output() uploadSuccess = new EventEmitter<File>();

  @ViewChild('fileUpload') fileUploadComponent!: FileUpload;

  public uploadedFile: File | null = null;
  public errorMessage: string = '';
  public isUploading: boolean = false;
  public readonly acceptedFileTypes: string[] = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls
    'text/csv', // .csv
    'application/csv' // .csv alternative
  ];
  public readonly acceptedExtensions: string[] = ['.xlsx', '.xls', '.csv'];
  public readonly maxFileSize: number = 10 * 1024 * 1024; // 10MB

  constructor(private messageService: MessageService) {}

  ngOnInit(): void {}

  public onFileSelect(event: any): void {
    this.errorMessage = '';
    const files = event.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    this.validateFile(file);
  }

  public onFileClear(): void {
    console.log('onFileClear');
    this.uploadedFile = null;
    this.errorMessage = '';
  }

  public onFileRemove(event: any): void {
    console.log('onFileRemove', event);
    this.uploadedFile = null;
    this.errorMessage = '';
  }

  public clearFile(): void {
    if (this.fileUploadComponent) {
      this.fileUploadComponent.clear();
    }
    this.uploadedFile = null;
    this.errorMessage = '';
  }

  public validateFile(file: File): void {
    this.errorMessage = '';

    // Check file extension
    const fileExtension = this.getFileExtension(file.name);
    if (!this.acceptedExtensions.includes(fileExtension.toLowerCase())) {
      this.errorMessage = `Invalid file type. Please upload ${this.acceptedExtensions.join(' or ')} files only.`;
      this.uploadedFile = null;
      return;
    }

    // Check file size
    if (file.size > this.maxFileSize) {
      const maxSizeMB = this.maxFileSize / (1024 * 1024);
      this.errorMessage = `File size exceeds the maximum limit of ${maxSizeMB}MB.`;
      this.uploadedFile = null;
      return;
    }

    // Check MIME type (optional, as browsers may report different MIME types)
    if (file.type && !this.acceptedFileTypes.includes(file.type)) {
      // Allow if extension is valid (some browsers may not set MIME type correctly)
      const extension = this.getFileExtension(file.name);
      if (!this.acceptedExtensions.includes(extension.toLowerCase())) {
        this.errorMessage = `Invalid file type. Please upload ${this.acceptedExtensions.join(' or ')} files only.`;
        this.uploadedFile = null;
        return;
      }
    }

    // File is valid
    this.uploadedFile = file;
    this.errorMessage = '';
  }

  public onError(event: any): void {
    if (event.error) {
      this.errorMessage = event.error;
    } else {
      this.errorMessage = 'An error occurred while uploading the file.';
    }
    this.uploadedFile = null;
  }

  public uploadFile(): void {
    if (!this.uploadedFile) {
      this.errorMessage = 'Please select a file to upload.';
      return;
    }

    // Re-validate before upload
    this.validateFile(this.uploadedFile);
    if (this.errorMessage) {
      return;
    }

    this.isUploading = true;
    this.errorMessage = '';

    // Store file reference before async operation
    const fileToUpload = this.uploadedFile;

    // TODO: Implement actual upload service call
    // For now, emit the file to parent component
    setTimeout(() => {
      this.isUploading = false;
      if (fileToUpload) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'File uploaded successfully.'
        });
        this.uploadSuccess.emit(fileToUpload);
        this.closeDialog();
      }
    }, 1000);
  }

  public closeDialog(): void {
    this.uploadedFile = null;
    this.errorMessage = '';
    this.isUploading = false;
    this.hideDialog.emit(false);
  }

  private getFileExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return '';
    }
    return filename.substring(lastDotIndex);
  }

  public getFileSizeString(size: number): string {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else {
      return (size / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }
}

